import { ICommonFormConfig } from '@app/components/form/common-form/common-form.types';
import {
  FieldList,
  IFormGroupRules,
} from '@app/components/form/services/schema-form/schema-form-interfaces';
import { XVIHeaderIconRendererComponent } from '@app/components/xvi-grid/custom-components/header-icon-renderer/header-icon-renderer.component';
import { HawkerSessionEditContainerComponent } from '@app/containers/hawker-session/containers/edit/edit.hawker-session.container';
import { I18n } from '@app/i18n/strings-keys';
import {
  getListGridRowCellClass,
  updateGridRowHeightNameWrap,
} from '@app/shared/helpers/ag-grid-helpers/ag-grid-helpers';
import { rx } from '@app/shared/rx/rx-wrapper';
import {
  BUTTON_FLEX_ORDER,
  CELL_FILTER,
  CELL_RENDERER,
  DEFAULT_GRID_HEIGHT,
  XUX_GRID_ACTIONS,
} from '@app/shared/types';
import { WIDGET_COMPONENT } from '@app/shared/types/enums/widget-component.enum';
import { getExpandableIconType } from '@app/shared/utils/common-utils';
import { TOOLBAR_BUTTON_POSITION } from '@geniuspos/ui-shared/modules/xdm-shared-components/definitions/types/toolbar-button.type';
import { get, isEmpty, map, pick } from 'lodash';
import { HawkerSessionFormService } from './hawker-session.form.service';

export const createRightFormConfig = (
  formService: HawkerSessionFormService,
  editor: HawkerSessionEditContainerComponent,
): ICommonFormConfig => {
  const sections: FieldList = {
    add_products_section: {
      widget: WIDGET_COMPONENT.panel_group,
      class: 'expand-height-to-parent-section',
    },
  };

  const panelGroups: IFormGroupRules[] = [
    {
      name: I18n.Forms.HawkerSessionForm.AddProducts,
      fields: ['add_products_section'],
    },
    {
      id: 'add_products_section',
      fields: ['product_list'],
    },
  ];

  const formKey = formService.formKeys.addProduct;

  const rightExpandedConfig = {
    showCaption: true,
    RTL: true,
    caption: I18n.Forms.HawkerSessionForm.AddProduct,
    imageTypeGetter: getExpandableIconType,
  };

  const gridIdentifyClass = 'expand-height-to-parent';

  return {
    formKey,
    schema: { properties: { product_list: { type: 'array' } } },
    formRules: {
      fields: {
        ...sections,
        product_list: {
          title: I18n.Forms.HawkerSessionForm.AddProduct,
          ignoreAutoTitleWhenTranslate: true,
          class: gridIdentifyClass,
          widget: WIDGET_COMPONENT.edit_grid_custom,
          widgetData: {
            showSearch: true,
            disableColumnSelector: true,
            orderControls: false,
            useVisibleItemCountDescription: true,
            itemsLabel: I18n.EntityNames.ProductSingularAndPlural,
            showLoader: formService.isRequestingProducts$,
            gridHeight: '100%',
            gridMinHeight: DEFAULT_GRID_HEIGHT,
            buttons: [
              {
                caption: I18n.Forms.HawkerSessionForm.AddProduct,
                expandable: true,
                expandIconConfigObs: editor.isFormExpandedObs(formKey).pipe(
                  rx.map((expanded) => {
                    return {
                      ...pick(rightExpandedConfig, ['RTL', 'imageTypeGetter']),
                      collapse: !expanded,
                    };
                  }),
                ),
                position: TOOLBAR_BUTTON_POSITION.HEADER,
                click: (_, collapse) => {
                  editor.rightExpanded$.next({
                    key: formKey,
                    expanded: !collapse,
                  });
                },
              },
              {
                caption: I18n.Forms.HawkerSessionForm.AddToRequestList,
                flexOrder: BUTTON_FLEX_ORDER.LEFTMOST,
                value: 'add_product_to_request',
                hidden: (selectedItems): boolean => isEmpty(selectedItems),
                click: (selectedItems) => {
                  formService.addProducts(
                    editor,
                    map(selectedItems, 'entity_id'),
                  );
                },
              },
            ],
            columns: [
              {
                title: I18n.EntityNames.Product,
                path: 'name',
                cellClass: 'cell-wrap-text',
                defaultView: true,
                sortable: false,
                headerComponentFramework: XVIHeaderIconRendererComponent,
                autoHeight: true,
                filterParams: {
                  quickFilter: true,
                },
                headerComponentParams: {
                  displayName: formService.i18nService.instant(
                    I18n.EntityNames.Product,
                  ),
                  headerIconClick: (isShrink) =>
                    updateGridRowHeightNameWrap({
                      isShrink,
                      gridIdentifyClass,
                      xuxGrid: formService.addProductXuxGrid,
                    }),
                },
                minWidth: 150,
              },
              {
                title: I18n.Forms.GridForm.ProductID,
                path: 'product_id',
                width: 150,
                minWidth: 150,
                defaultView: true,
                sortable: true,
                filterType: CELL_FILTER.string,
              },
              {
                title: I18n.Common.Add,
                path: 'add',
                width: 80,
                minWidth: 80,
                maxWidth: 80,
                defaultView: true,
                cellRenderer: CELL_RENDERER.icon_button_renderer,
                cellRendererParams: {
                  icon: 'plus',
                  click: (event) => {
                    formService.addProducts(editor, [
                      get(event, 'data.entity_id'),
                    ]);
                  },
                },
              },
            ],
            userGridOptions: {
              suppressHorizontalScroll: false,
              suppressClickEdit: true,
              isRowSelectable: () => !formService.isFormDisabled$.getValue(),
              overlayNoRowsTemplate: formService.i18nService.instant(
                I18n.Common.NoRowsToShow,
              ),
              rowClassRules: {
                ...getListGridRowCellClass(),
              },
            },
            eventHandlers: {
              [XUX_GRID_ACTIONS.GRID_READY]:
                formService.onAddProductGridReadyHandler.bind(formService),
            },
          },
        },
      },
      groups: [...panelGroups],
      defaultGroup: false,
    },
    expandIconConfig: rightExpandedConfig,
    class: 'expand-height-form-panel',
  };
};
